import { SessionService } from './../../../../../shared/src/lib/service/session.service';
import { Injectable, NgZone, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import {
  CustomModalService,
  DropMessageTypeEnum,
  ExpireSoonDescriptionStr,
  ExpireSoonStr,
  LOGOUT_TEXT,
  LOGOUT_TITLE,
  MAJOR_VERSION_STORAGE_KEY,
  RedirectService,
  SessionExpiredMessage,
  trialActiveStr,
  VERSION_STORAGE_KEY,
} from '@piqet-angular/shared';
import { of } from 'rxjs';
import { map, exhaustMap, catchError, tap, switchMap } from 'rxjs/operators';
import { AuthenticationService } from '../../services/auth/authentication.service';
import * as AuthActions from './auth.actions';
import * as UserDetailsActions from '../user-details/user-details.actions';
import * as sharedAction from '../shared/shared.actions';
import { DashboardFacade } from '../dashboard/dashboard.facade';
import { NgxSpinnerService } from 'ngx-spinner';
import { CookieService } from 'ngx-cookie-service';
import * as jwt_decode from 'jwt-decode';
import { MenuService } from 'libs/features/src/lib/menu/menu.service';
import {
  AuthFacade,
  CookieHelperService,
  SimultaneousService,
  UserDetailsFacade,
} from '@piqet-angular/data';
import { SharedService } from '../../services/shared/shared.service';
import { SwalService } from 'libs/shared/src/lib/service/swal.service';
import { Store } from '@ngrx/store';
import { LoginAttemptService } from '@piqet-angular/features';
import Swal from 'sweetalert2';

import { FeatureConfigService } from 'libs/shared/src/lib/service/feature-config.service';

import { CommonService } from 'libs/shared/src/lib/service/common.service';

@Injectable()
export class AuthEffects {
  private actions$ = inject(Actions);
  //Effects for login user.
  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.login),
      exhaustMap(action => {
        return this.authenticationService.login(action.payload).pipe(
          switchMap(async (data: any) => {
            let isLoggedIn = !!data.data.token;
            this.loginAttemptService.resetAttempts();
            const licenseType = data.data?.licenseType ?? 0;
            sessionStorage.setItem('licenseType', licenseType.toString());
            switch (data.messageType) {
              case DropMessageTypeEnum.directLogin:
                await this.loginSuccessMethod(data);
                return AuthActions.loginSuccess({
                  profile: data.data.applicationUser,
                  tokenClaim: data.data.token,
                  isLoggedIn: isLoggedIn,
                  isFlag: data.data?.userDetail?.isFlag,
                  sessionId: data.data.sessionId,
                });
                break;
              case DropMessageTypeEnum.MFAEnabled:
                return AuthActions.showOtpPage({ showOtpPage: true, userId: data.data.userId });
              default:
                return AuthActions.loginFailure({
                  error: data.message,
                  messageType: data.messageType,
                });
                break;
            }
          }),
          catchError((error: any) => {
            this.loginAttemptService.incrementAttempts();
            if (this.loginAttemptService.isMaxAttemptsReached()) {
              return of(AuthActions.maxLoginAttemptsReached());
            }

            return of(AuthActions.loginFailure(error));
          })
        );
      })
    )
  );

  loginSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginSuccess),
      exhaustMap(action => {
        if (!action.isFlag) {
          this.router.navigate(['/secure-password']);
        } else {
          // Check for stored redirect URL
          const redirectUrl = this.redirectService.getAndClearRedirectUrl();
          if (redirectUrl) {
            this.router.navigateByUrl(redirectUrl);
          } else {
            this.router.navigate(['/dashboard']);
          }
          if (!action.isTokenUpdate) {
            return of(sharedAction.ToasterSuccess({ description: 'Logged in successfully.' }));
          }
        }
        return of();
      })
    )
  );

  loginFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginFailure),
      exhaustMap(() => {
        return of(sharedAction.hideShowLoader({ isLoaded: true }));
      })
    )
  );

  verifyOtp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.verifyOtp),
      exhaustMap(action => {
        const payload = {
          otp: action.otpNumber,
          userId: action.userId,
          ipAddress: action.ipAddress,
        };
        return this.authenticationService.verifyOtp(payload).pipe(
          switchMap(async (data: any) => {
            if (data.messageType === DropMessageTypeEnum.Error) {
              return AuthActions.verifyOtpFailure({
                error: data.message,
                messageType: data.messageType,
              });
            }
            await this.loginSuccessMethod(data);
            this.spinnerService.hide();
            return AuthActions.loginSuccess({
              profile: data.data.applicationUser,
              tokenClaim: data.data.token,
              isLoggedIn: !!data.data.token,
              isFlag: data.data?.userDetail?.isFlag,
              sessionId: data.data.sessionId,
            });
          }),
          catchError((error: any) =>
            of(AuthActions.verifyOtpFailure({ error: error, messageType: null }))
          )
        );
      })
    )
  );
  verifyOtpFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.verifyOtpFailure),
      exhaustMap(() => {
        return of(sharedAction.hideShowLoader({ isLoaded: true }));
      })
    )
  );
  //Effects for register user.
  register$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.register),
      exhaustMap(action =>
        this.authenticationService.register(action.payload, action.purchaseType).pipe(
          exhaustMap((data: any) => {
            const messageType = data?.messageType;

            if (messageType === 2) {
              return of(
                sharedAction.ToasterError({
                  description: data?.message,
                }),
                sharedAction.hideShowLoader({ isLoaded: true })
              );
            }

            return of(
              AuthActions.registerSuccess({
                isRegistered: data,
                purchaseType: action.purchaseType,
              })
            );
          }),
          catchError((error: any) => of(AuthActions.registerFailure(error)))
        )
      )
    )
  );

  //Effects for Ip of user.
  getpAdress$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.getIpAddress),
      exhaustMap(action => {
        return this.authenticationService.getIPAddress().pipe(
          map((data: any) => {
            return AuthActions.getIpAddressSuccess({
              ipAdressData: data,
            });
          }),
          catchError((error: any) => of(AuthActions.getIpAddressFailuer(error)))
        );
      })
    )
  );

  registerSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.registerSuccess),
      exhaustMap(data => {
        if (data.purchaseType == 'purchase') {
          window.location.href = data.isRegistered.data.value;
          return of(sharedAction.hideShowLoader({ isLoaded: true }));
        } else if (data.purchaseType == 'trypiqet') {
          this.router.navigate([`/${trialActiveStr}`]);
          return of(sharedAction.hideShowLoader({ isLoaded: true }));
        } else {
          this.router.navigate(['/login']);
          return of(
            sharedAction.ToasterSuccess({ description: 'Thank you for signing up with PIQET' })
          );
        }
      })
    )
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logout),
      exhaustMap(action => {
        let timerInterval: any;
        Swal.fire({
          title: LOGOUT_TITLE,
          html: LOGOUT_TEXT,
          icon: 'warning',
          timer: 10000,
          timerProgressBar: true,
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          showCancelButton: false,
          customClass: 'Popupbackground simultaneous-login',
          didOpen: () => {
            const timer = Swal.getPopup()?.querySelector('b');
            timerInterval = setInterval(() => {
              if (timer) {
                timer.textContent = `${Math.ceil(Swal.getTimerLeft()! / 500)}`;
              }
            }, 1000);
          },
          willClose: () => {
            clearInterval(timerInterval);
          },
        });
        this.sessionService.stopConnection();
        const storedVersion = localStorage.getItem(VERSION_STORAGE_KEY);
        const storedMajorVersion = localStorage.getItem(MAJOR_VERSION_STORAGE_KEY);
        // Clear redirect URL on logout
        this.redirectService.clearRedirectUrl();
        localStorage.clear();
        Swal.close();
        this.cookieService.deleteAll();
        this.cookieService.delete('refreshToken', '/', '', true, 'Strict');
        this.dashboardFacade.clearDashboradData();
        this.menuService.clearMenu();
        sessionStorage.clear();
        this.simultaneousService.resetLoginMethod();
        this.userDetailsFacade.clearUserProfileDetails();
        this.authFacade.clearLoginData();
        this.userDetailsFacade.clearUserProfileImg();
        this.sharedService.triggerRemoveBeaconScript();
        this.customModalService.close();
        const beaconContainer = document.getElementById('beacon-container');
        if (beaconContainer && beaconContainer.parentNode) {
          beaconContainer.parentNode.removeChild(beaconContainer);
        }

        localStorage.setItem(VERSION_STORAGE_KEY, storedVersion ?? '1.0.0');
        localStorage.setItem(MAJOR_VERSION_STORAGE_KEY, storedMajorVersion ?? '1.0.0');
        return of(AuthActions.logoutSuccess({ isLoggedOut: false }));
      })
    )
  );

  logoutSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logoutSuccess),
        tap(() => {
          this.ngZone.run(() => {
            this.router.navigate(['/login']);
          });
        })
      ),
    { dispatch: false }
  );

  //Effects for forgot user.
  forgotPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.forgotPassword),
      exhaustMap(action => {
        return this.authenticationService.forgotPassword(action.payload).pipe(
          map((data: any) => {
            return AuthActions.forgotPasswordSuccess({
              forgotPasswordData: data,
              isForgotPassword: data.data,
            });
          }),

          catchError((error: any) =>
            of(
              AuthActions.forgotPasswordFailure(error),
              sharedAction.hideShowLoader({ isLoaded: true })
            )
          )
        );
      })
    )
  );

  forgotPasswordSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.forgotPasswordSuccess),
      exhaustMap(forgotPasswordData => {
        if (forgotPasswordData.isForgotPassword) {
          this.router.navigate(['/login']);

          return of(sharedAction.hideShowLoader({ isLoaded: true }));
        } else {
          return of(sharedAction.hideShowLoader({ isLoaded: true }));
        }
      })
    )
  );

  //Effects for reset user.
  resetPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.resetPassword),
      exhaustMap(action => {
        return this.authenticationService.resetPassword(action.payload).pipe(
          map((data: any) => {
            if (data.messageType == DropMessageTypeEnum.Success) {
              return AuthActions.resetPasswordSuccess({
                resetPasswordData: data,
              });
            } else {
              return AuthActions.resetPasswordFailure(data);
            }
          }),
          catchError((error: any) => of(AuthActions.resetPasswordFailure(error)))
        );
      })
    )
  );

  //Effects for changePassword .
  changePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.changePassword),
      exhaustMap(action => {
        return this.authenticationService.changePassword(action.payload).pipe(
          map((data: any) => {
            if (!data.messageType) {
              return AuthActions.changePasswordSuccess({
                changePasswordData: data,
                isForcePassword: action.payload?.isForcePassword,
                passwordUpdated: true,
              });
            } else {
              return sharedAction.ToasterError({ description: data?.message });
            }
          }),
          catchError((error: any) => of(AuthActions.changePasswordFailure(error)))
        );
      })
    )
  );

  changePasswordSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.changePasswordSuccess),
      exhaustMap(action => {
        if (action.changePasswordData.messageType === 0) {
          if (action?.isForcePassword) {
            this.cookieService.set('forceResetPasswordDisable', 'true');
            setTimeout(() => {
              // Check for stored redirect URL after password change
              const redirectUrl = this.redirectService.getAndClearRedirectUrl();
              if (redirectUrl) {
                this.router.navigateByUrl(redirectUrl);
              } else {
                this.router.navigate(['/dashboard']);
              }
            }, 2000);
          }
          return of(
            sharedAction.ToasterSuccess({ description: action.changePasswordData.message })
          );
        } else {
          return of(sharedAction.ToasterError({ description: action.changePasswordData.message }));
        }
      })
    )
  );

  resetPasswordSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.resetPasswordSuccess),
      exhaustMap(() => {
        this.router.navigate(['/login']);
        return of(sharedAction.ToasterSuccess({ description: 'Password changed successfully.' }));
      })
    )
  );

  resetPasswordFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.resetPasswordFailure),
      exhaustMap((action: any) => {
        return of(sharedAction.ToasterError({ description: action.message }));
      })
    )
  );

  //Effects for reset user.
  activeUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.activeUser),
      exhaustMap(action => {
        let serviceCall$;

        switch (action.activationType) {
          case 'uid':
            serviceCall$ = this.authenticationService.activateByUID(action.payload);
            break;

          case 'trialCode':
            serviceCall$ = this.authenticationService.activateByTrialCode(action.payload);
            break;

          default:
            serviceCall$ = this.authenticationService.activeUser(action.payload);
            break;
        }

        return serviceCall$.pipe(
          map((data: any) =>
            AuthActions.activeUserSuccess({
              activeUSerStatus: data?.message,
            })
          ),
          catchError((error: any) => of(AuthActions.activeUserFailure(error)))
        );
      })
    )
  );

  //Effects for payment done
  payment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.payment),
      exhaustMap(action => {
        return this.authenticationService.thanks(action.payload).pipe(
          map((data: any) => {
            return AuthActions.paymentSuccess({
              paymentStatus: data,
            });
          }),
          catchError((error: any) => of(AuthActions.paymentFailure(error)))
        );
      })
    )
  );

  async loginSuccessMethod(data: any) {
    const tokenExpiry = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
    this.cookieHelper.setCookies(data, tokenExpiry);
    this.simultaneousService.logoutWarningPopup(data, true);
    if (data.data.refreshToken) {
      this.cookieService.set(
        'refreshToken',
        data.data.refreshToken,
        tokenExpiry,
        '/',
        '',
        true,
        'Strict'
      );
    }
    const decodedToken: any = jwt_decode.jwtDecode(data.data.token);
    const userId = decodedToken.Id;
    if (userId) {
      if (data.data.deviceId) {
        localStorage.setItem('deviceId', data.data.deviceId);
      }
      this.sessionService.initializeConnection(this.commonService.initDeviceId());
      this.sessionService.notifyLoginSuccess(data.data.token, tokenExpiry);
    }
    // const warningTime = expiry.getTime() - 5 * 60 * 1000;
    // console.table([
    //   {
    //     'Event': 'Token Expiry',
    //     'Time': expiry.toLocaleString(),
    //     'In Minutes': ((expiry.getTime() - Date.now())/60000).toFixed(2)
    //   },
    //   {
    //     'Event': 'Warning Time',
    //     'Minutes': Math.floor((expiry.getTime() - Date.now() - 5 * 60 * 1000) / 60000),
    //     'Seconds': Math.floor(((expiry.getTime() - Date.now() - 5 * 60 * 1000) % 60000) / 1000)
    //   }
    // ]);
    // const currentTime = Date.now();

    // if (warningTime > currentTime) {
    //   const timeUntilWarning = warningTime - currentTime;
    //   console.table([{
    //     'Event': 'Session Expiry',
    //     'Minutes': (timeUntilWarning / 60000).toFixed(2)
    //   }]);
    //   console.log('Warning Time:', new Date(warningTime).toLocaleString());
    //   setTimeout(() => {
    //     const token = this.cookieService.get('token');
    //     console.log(`Token  ${token} `);
    //     if (token) {
    //       this.swalService.confirm(ExpireSoonStr, ExpireSoonDescriptionStr).then(confirmed => {
    //         if (confirmed) {
    //           const token = this.cookieService.get('token');
    //           if (!token) {
    //             console.log(SessionExpiredMessage)
    //             return of(sharedAction.ToasterError({ description: SessionExpiredMessage }));
    //           };
    //           this.store.dispatch(
    //             UserDetailsActions.regenerateAccessToken({ sessionString: 'sessionString' })
    //           );
    //         }
    //       });
    //     }
    //   }, timeUntilWarning);
    // }
    await this.sessionService.getAccessModuleData();
    this.spinnerService.hide();
  }
  constructor(
    private authenticationService: AuthenticationService,
    private dashboardFacade: DashboardFacade,
    private loginAttemptService: LoginAttemptService,
    private router: Router,
    private menuService: MenuService,
    private modal: CustomModalService,
    private spinnerService: NgxSpinnerService,
    private cookieService: CookieService,
    private userDetailsFacade: UserDetailsFacade,
    private authFacade: AuthFacade,
    private sharedService: SharedService,
    private customModalService: CustomModalService,
    private authService: AuthenticationService,
    private swalService: SwalService,
    private store: Store,
    private cookieHelper: CookieHelperService,
    private simultaneousService: SimultaneousService,
    private featureConfigService: FeatureConfigService,
    private sessionService: SessionService,
    private commonService: CommonService,
    private ngZone: NgZone,
    private redirectService: RedirectService
  ) {}
}
